import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faUpload, faTimes, faImage } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { uploadImageToAzure } from "../../../../services/AzureStorageService";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const priorities = [
    { priority_id: 1, priority_name: "Low", priority_code: "LOW" },
    { priority_id: 2, priority_name: "Medium", priority_code: "MEDIUM" },
    { priority_id: 3, priority_name: "High", priority_code: "HIGH" },
    { priority_id: 4, priority_name: "Critical", priority_code: "CRITICAL" }
];

const AnnouncementForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const isEditMode = Boolean(id);
    const [submitting, setSubmitting] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(isEditMode);
    const [roleList, setRoleList] = useState([]);

    const [uploadingFiles, setUploadingFiles] = useState([]); // List of File objects with local previews
    const [existingAttachments, setExistingAttachments] = useState([]); // Array of existing attachments

    const employeeCode = storageService.getItem("employeeCode") || "0";
    const roleId = Number(storageService.getItem("roleId")) || 0;
    const siteId = Number(storageService.getItem("siteId")) || Number(storageService.getItem("SiteId")) || 0;
    const employeeName = storageService.getItem("employeeName") || "Admin";

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        priorityId: 2, // Medium default (from priorities array: Medium has ID 2)
        publishDate: "", // Not prefilled as per instructions
        expiryDate: "",  // Not prefilled as per instructions
        targetRoles: [] // Array of { label, value }
    });

    const [errors, setErrors] = useState({});

    // Focus states for dynamic borders matching secondaryColor()
    const [focusedField, setFocusedField] = useState("");
    const [isFileHovered, setIsFileHovered] = useState(false);
    const [isFileFocused, setIsFileFocused] = useState(false);

    useEffect(() => {
        const handleClickOutside = (e) => {
            const dragContainer = document.getElementById("drag-container");
            if (dragContainer && !dragContainer.contains(e.target)) {
                setIsFileFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch roles list
    const fetchRoles = async () => {
        try {
            const res = await apiServices({ method: "GET", url: "/RoleMaster/GetAllRoles" });
            if (res?.success) {
                setRoleList(res.data?.data || []);
                return res.data?.data || [];
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
        return [];
    };

    useEffect(() => {
        const initialize = async () => {
            const roles = await fetchRoles();

            if (isEditMode) {
                const loadData = (notice, currentRoles) => {
                    const mapRolesToOptions = (rawRoles) => {
                        if (!rawRoles || !Array.isArray(rawRoles)) return [];
                        return rawRoles.map(item => {
                            const rid = (typeof item === 'object' && item !== null)
                                ? (item.roleId || item.RoleId)
                                : item;
                            const matched = currentRoles.find(r => Number(r.RoleId) === Number(rid));
                            return matched
                                ? { label: matched.RoleName, value: matched.RoleId }
                                : { label: (typeof item === 'object' && item !== null) ? (item.roleName || item.RoleName || `Role ${rid}`) : `Role ${rid}`, value: rid };
                        });
                    };

                    const formatForDateTimeLocal = (isoString) => {
                        if (!isoString) return "";
                        const date = new Date(isoString);
                        if (isNaN(date.getTime())) return "";
                        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
                        return date.toISOString().slice(0, 16);
                    };

                    let rawRolesList = notice.targetRoles || notice.TargetRoles || notice.targetRoleIds || notice.TargetRoleIds || [];
                    if ((!rawRolesList || rawRolesList.length === 0) && (notice.targetMappings || notice.TargetMappings)) {
                        const mappings = notice.targetMappings || notice.TargetMappings || [];
                        let roleIds = [];
                        mappings.forEach(m => {
                            const rids = m.roleId || m.RoleId || [];
                            if (Array.isArray(rids)) {
                                roleIds = [...roleIds, ...rids];
                            } else if (rids !== null && rids !== undefined) {
                                roleIds.push(rids);
                            }
                        });
                        rawRolesList = roleIds;
                    }

                    setFormData({
                        title: notice.title || notice.Title || "",
                        description: notice.description || notice.Description || "",
                        priorityId: Number(notice.priorityId || notice.PriorityId || 2),
                        publishDate: formatForDateTimeLocal(notice.publishDate || notice.PublishDate),
                        expiryDate: formatForDateTimeLocal(notice.expiryDate || notice.ExpiryDate),
                        targetRoles: mapRolesToOptions(rawRolesList)
                    });

                    const rawAttachments = notice.attachments || notice.Attachments || [];
                    setExistingAttachments(rawAttachments.map(att => ({
                        fileName: att.fileName || att.FileName || "",
                        filePath: att.filePath || att.FilePath || ""
                    })));
                };

                const fetchNoticeDetails = async () => {
                    setLoadingDetails(true);
                    try {
                        const detailRes = await apiServices({
                            url: `/CommBroadcast/notice/getNoticeById/${id}`,
                            method: "GET"
                        });
                        if (detailRes.success && detailRes.data) {
                            loadData(detailRes.data, roles);
                            setLoadingDetails(false);
                            return;
                        }
                    } catch (error) {
                        console.warn("Single GET notice endpoint failed/missing, falling back to list GET:", error);
                    }

                    // Fallback to fetch notices and find by id
                    try {
                        const params = {
                            userId: employeeCode,
                            roleId: roleId,
                            siteId: siteId,
                            pageNumber: 1,
                            pageSize: 100
                        };
                        const res = await apiServices({
                            url: "/CommBroadcast/notice/getNotices",
                            method: "GET",
                            params: params
                        });
                        if (res.success) {
                            const list = res.data?.data || res.data?.notices || res.data || [];
                            const notice = list.find(n => String(n.id || n.Id || n.noticeId) === String(id));
                            if (notice) {
                                loadData(notice, roles);
                            } else {
                                toast.error("Announcement not found");
                                navigate("/zentres/announcement");
                            }
                        }
                    } catch (error) {
                        console.error("Error fetching notice details list fallback:", error);
                        toast.error("Failed to load announcement details");
                        navigate("/zentres/announcement");
                    } finally {
                        setLoadingDetails(false);
                    }
                };

                fetchNoticeDetails();
            }
        };

        initialize();
    }, [isEditMode, id]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleSelectChange = (id, option) => {
        setFormData(prev => ({ ...prev, [id]: option ? option.value : null }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleMultiSelectChange = (selectedOptions) => {
        setFormData(prev => ({ ...prev, targetRoles: selectedOptions || [] }));
        if (errors.targetRoles) {
            setErrors(prev => ({ ...prev, targetRoles: null }));
        }
    };

    // Handle Image upload selection
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const newFiles = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            name: file.name
        }));

        setUploadingFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (index) => {
        setUploadingFiles(prev => {
            const copy = [...prev];
            URL.revokeObjectURL(copy[index].preview);
            copy.splice(index, 1);
            return copy;
        });
    };

    const removeExistingFile = (index) => {
        setExistingAttachments(prev => prev.filter((_, idx) => idx !== index));
    };

    const validate = () => {
        const tempErrors = {};
        if (!formData.title.trim()) tempErrors.title = "Title is required";
        if (!formData.description.trim()) tempErrors.description = "Description is required";
        if (!formData.publishDate) tempErrors.publishDate = "Publish Date is required";
        if (!formData.expiryDate) tempErrors.expiryDate = "Expiry Date is required";
        if (formData.targetRoles.length === 0) tempErrors.targetRoles = "At least one target role is required";

        if (formData.publishDate && formData.expiryDate) {
            const pub = new Date(formData.publishDate);
            const exp = new Date(formData.expiryDate);
            if (exp <= pub) {
                tempErrors.expiryDate = "Expiry date must be after the publish date";
            }
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.warning("Please correct the validation errors.");
            return;
        }

        setSubmitting(true);
        try {
            // 1. Upload attachments to Azure if present, then combine with existing
            const attachmentsPayload = [...existingAttachments];
            if (uploadingFiles.length > 0) {
                toast.info("Uploading attachments...");
                for (const fileObj of uploadingFiles) {
                    const uploadedUrl = await uploadImageToAzure(fileObj.file);
                    if (uploadedUrl) {
                        attachmentsPayload.push({
                            fileName: fileObj.name,
                            filePath: uploadedUrl
                        });
                    } else {
                        throw new Error(`Failed to upload attachment: ${fileObj.name}`);
                    }
                }
            }

            // 2. Format dates to ISO
            const pubDateIso = new Date(formData.publishDate).toISOString();
            const expDateIso = new Date(formData.expiryDate).toISOString();

            // 3. Build payload
            const currentUnitId = Number(storageService.getItem("unitId")) || Number(storageService.getItem("branchorunitID")) || Number(storageService.getItem("UnitId")) || 0;
            const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });

            let payload = {
                userId: Number(employeeCode),
                title: formData.title,
                description: formData.description,
                priorityId: Number(formData.priorityId),
                publishDate: pubDateIso,
                expiryDate: expDateIso,
                correlationId: uuid,
                targetRoleIds: formData.targetRoles.map(r => Number(r.value)),
                targetMappings: [
                    {
                        siteId: [Number(siteId)],
                        unitId: [Number(currentUnitId)],
                        roleId: formData.targetRoles.map(r => Number(r.value))
                    }
                ],
                attachments: attachmentsPayload
            };

            // 4. Hit appropriate endpoint
            let res;
            if (isEditMode) {
                payload = {
                    ...payload,
                    noticeId: Number(id),
                    modifiedBy: employeeName
                };
                res = await apiServices({
                    url: "/CommBroadcast/notice/update",
                    method: "POST",
                    data: payload
                });
            } else {
                payload = {
                    ...payload,
                    createdBy: employeeName
                };
                res = await apiServices({
                    url: "/CommBroadcast/notice/create",
                    method: "POST",
                    data: payload
                });
            }

            if (res.success) {
                toast.success(isEditMode ? "Announcement updated successfully!" : "Announcement created successfully in draft mode!");
                navigate("/zentres/announcement");
            } else {
                toast.error(res.message || `Failed to ${isEditMode ? 'update' : 'create'} announcement`);
            }
        } catch (error) {
            console.error("Error saving announcement:", error);
            toast.error(error.message || "An error occurred while saving announcement");
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingDetails) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-dashboardbg rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[#0F172A] dark:text-textprimary text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={isEditMode ? "Update Announcement" : "Create Announcement"}
                subHeading={isEditMode ? "Modify the fields below to update the announcement." : "Fill out the details to broadcast a new notice or update."}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor flex flex-col gap-6 shadow-sm mb-4">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Notice Title */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="title">
                                Announcement Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                onFocus={() => setFocusedField("title")}
                                onBlur={() => setFocusedField("")}
                                placeholder="Enter announcement title"
                                className={`w-full h-10 px-4 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all placeholder:text-gray-400 focus:outline-none ${errors.title ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                    }`}
                                style={errors.title ? {} : (focusedField === "title" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                required
                            />
                            {errors.title && <p className="text-red-500 text-[10px] font-bold mt-0.5">{errors.title}</p>}
                        </div>

                        {/* Target Roles */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                isMulti={true}
                                label={<span className="block text-xs font-bold text-gray-700 mb-1">Target Roles <span className="text-red-500">*</span></span>}
                                options={roleList.map(r => ({ label: r.RoleName, value: r.RoleId }))}
                                value={formData.targetRoles}
                                onChange={handleMultiSelectChange}
                                placeholder="Select target roles"
                                error={errors.targetRoles}
                                errorMessage={errors.targetRoles}
                            />
                        </div>

                        {/* Priority */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="block text-xs font-bold text-gray-700 mb-1">Priority <span className="text-red-500">*</span></span>}
                                options={priorities.map(p => ({ label: p.priority_name, value: p.priority_id }))}
                                value={priorities.find(p => p.priority_id === formData.priorityId)
                                    ? { label: priorities.find(p => p.priority_id === formData.priorityId).priority_name, value: formData.priorityId }
                                    : null}
                                onChange={(opt) => handleSelectChange("priorityId", opt)}
                                placeholder="Select priority"
                            />
                        </div>

                        {/* Publish Date */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="publishDate">
                                Publish Date & Time <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                id="publishDate"
                                value={formData.publishDate}
                                onChange={handleInputChange}
                                onFocus={() => setFocusedField("publishDate")}
                                onBlur={() => setFocusedField("")}
                                className={`w-full h-10 px-4 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all focus:outline-none ${errors.publishDate ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                    }`}
                                style={errors.publishDate ? {} : (focusedField === "publishDate" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                required
                            />
                            {errors.publishDate && <p className="text-red-500 text-[10px] font-bold mt-0.5">{errors.publishDate}</p>}
                        </div>

                        {/* Expiry Date */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="expiryDate">
                                Expiry Date & Time <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                id="expiryDate"
                                value={formData.expiryDate}
                                onChange={handleInputChange}
                                onFocus={() => setFocusedField("expiryDate")}
                                onBlur={() => setFocusedField("")}
                                className={`w-full h-10 px-4 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all focus:outline-none ${errors.expiryDate ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                    }`}
                                style={errors.expiryDate ? {} : (focusedField === "expiryDate" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                required
                            />
                            {errors.expiryDate && <p className="text-red-500 text-[10px] font-bold mt-0.5">{errors.expiryDate}</p>}
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="description">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                onFocus={() => setFocusedField("description")}
                                onBlur={() => setFocusedField("")}
                                placeholder="Write the announcement description..."
                                rows="5"
                                className={`w-full px-4 py-3 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all placeholder:text-gray-400 resize-none focus:outline-none ${errors.description ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                    }`}
                                style={errors.description ? {} : (focusedField === "description" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                required
                            />
                            {errors.description && <p className="text-red-500 text-[10px] font-bold mt-0.5">{errors.description}</p>}
                        </div>

                        {/* Image Attachments */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                Image Attachments <span className="text-gray-400 font-normal text-[10px]">(Optional)</span>
                            </label>

                            <div
                                id="drag-container"
                                className="border-2 border-dashed rounded-md p-6 bg-transparent dark:bg-transparent border-gray-200 dark:border-bordercolor transition-all flex flex-col items-center justify-center cursor-pointer relative group"
                                onMouseEnter={() => setIsFileHovered(true)}
                                onMouseLeave={() => setIsFileHovered(false)}
                                onClick={() => setIsFileFocused(true)}
                                style={(isFileHovered || isFileFocused)
                                    ? { borderColor: secondaryColor(), boxShadow: isFileFocused ? `0 0 0 2px ${secondaryColor()}40` : "none" }
                                    : {}}
                            >
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                />
                                <div className="flex flex-col items-center gap-2">
                                    <FontAwesomeIcon icon={faUpload} className="text-gray-400 group-hover:text-emerald-500 text-2xl transition-colors" />
                                    <span className="text-xs font-bold text-gray-600">Drag & drop files or click to upload</span>
                                    <span className="text-[10px] text-gray-400">Supported formats: PNG, JPG, JPEG, WEBP</span>
                                </div>
                            </div>

                            {/* Existing attachments */}
                            {existingAttachments.length > 0 && (
                                <div className="flex flex-col gap-2 mt-4 bg-sectionheaderbg p-4 rounded-xl border border-gray-100 dark:border-bordercolor">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Existing Uploaded Images</span>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 mt-2">
                                        {existingAttachments.map((att, index) => (
                                            <div key={`existing-${index}`} className="relative w-full aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-bordercolor flex items-center justify-center bg-gray-50 dark:bg-actionitembg group">
                                                <img
                                                    src={att.filePath}
                                                    alt={att.fileName}
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeExistingFile(index)}
                                                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                                </button>
                                                <div className="absolute bottom-0 inset-x-0 bg-black/40 text-[9px] text-white px-2 py-0.5 truncate text-center">
                                                    {att.fileName}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Newly Selected Preview */}
                            {uploadingFiles.length > 0 && (
                                <div className="flex flex-col gap-2 mt-4 bg-sectionheaderbg p-4 rounded-xl border border-gray-100 dark:border-bordercolor">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">New Selected Images (Draft)</span>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 mt-2">
                                        {uploadingFiles.map((fileObj, index) => (
                                            <div key={index} className="relative w-full aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-bordercolor flex items-center justify-center bg-gray-50 dark:bg-actionitembg group">
                                                <img
                                                    src={fileObj.preview}
                                                    alt={fileObj.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index)}
                                                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                                </button>
                                                <div className="absolute bottom-0 inset-x-0 bg-black/40 text-[9px] text-white px-2 py-0.5 truncate text-center">
                                                    {fileObj.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                    <button
                        type="button"
                        disabled={submitting}
                        onClick={() => navigate("/zentres/announcement")}
                        className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors disabled:opacity-50"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                e.currentTarget.style.color = "var(--color-primaryaccent)";
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : "Save Announcement"}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementForm;
